// ============================================================================
// SLIDESHOW COMPONENT
// ============================================================================

if (!customElements.get("slideshow-component")) {
  class SlideshowComponent extends HTMLElement {
    constructor() {
      super()
      this.selectors = {
        contents: ['.f-slideshow__content-wrapper'],
        selected: '.selected',
        flickity: 'flickity-component',
        pageDots: '.flickity-page-dots',
        pageCounter: '.flickity-page-counter'
      }
      this.domNodes = queryDomNodes(this.selectors, this)
      this.prevIndex = 0
      this.init()
    }

    disconnectedCallback() {
      clearInterval(this.check)
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    init() {
      this.check = setInterval(() => {
        this.slider = this.domNodes.flickity.slider && this.domNodes.flickity.slider.instance
        if (this.slider && typeof this.slider == 'object') {
          clearInterval(this.check)
          this.removeAttribute('data-media-loading')
          this.slider.on('change', this.handleChange.bind(this))
          this.domNodes.contents[0].classList.add('selected')
          this.handleScreenChange()
          this.playVideo()
          if (this.domNodes.pageCounter) {
            this.domNodes.flickity.insertBefore(this.domNodes.pageCounter, null)
          }
        }
      }, 100)
    }

    // ========================================================================
    // SLIDE CHANGE HANDLING
    // ========================================================================

    handleChange(index) {
      this.updateSliderControls(index)
      this.updateContentVisibility(index)
      this.playVideo()
    }

    updateSliderControls(index) {
      const sliderCounterCurrent = this.querySelector('.flickity-counter--current')
      const slideContentCurrent = this.domNodes.contents[index];
      const currentTextColor = slideContentCurrent.dataset.textColor;
      this.style.setProperty('--slider-controls-color', currentTextColor);

      // Handle page counter
      if (this.domNodes.pageCounter && sliderCounterCurrent) {
        sliderCounterCurrent.textContent = index + 1
      }
    }

    updateContentVisibility(index) {
      this.domNodes.contents.forEach(item => item.classList.remove('selected'))
      this.domNodes.contents[this.prevIndex].classList.add('f-slideshow__content--out')
      this.domNodes.contents[index].classList.add('selected')
      setTimeout(() => {
        this.domNodes.contents[this.prevIndex].classList.remove('f-slideshow__content--out')
        this.prevIndex = index
      }, 300)
    }

    // ========================================================================
    // MEDIA HANDLING
    // ========================================================================

    playVideo() {
      this.pauseAllMedia()
      const selectedElm = this.slider.selectedElement
      const deferredMedia = selectedElm.querySelector('deferred-media')
      if (deferredMedia) {
        deferredMedia.loadContent()
        const youtube = deferredMedia.querySelector('.js-youtube')
        const vimeo = deferredMedia.querySelector('.js-vimeo')
        const video = deferredMedia.querySelector('video')
        if (video) video.play()
        if (youtube) youtube.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*')
        if (vimeo) vimeo.contentWindow.postMessage('{"method":"play"}', '*')
      }
    }

    pauseAllMedia() {
      this.querySelectorAll(".js-youtube").forEach((video) => {
        video.contentWindow.postMessage(
          '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
          "*"
        );
      });
      this.querySelectorAll(".js-vimeo").forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', "*");
      });
      this.querySelectorAll("video").forEach((video) => video.pause());
    }

    // ========================================================================
    // RESPONSIVE HANDLING
    // ========================================================================

    handleScreenChange() {
      if (FoxThemeSettings.isMobile) {
        // this.domNodes.flickity.toggleFade()
      }
      document.addEventListener('matchMobile', () => {
        // this.domNodes.flickity.toggleFade()
      })
      document.addEventListener('unmatchMobile', () => {
        // this.domNodes.flickity.toggleFade(true)
      })
    }

    // ========================================================================
    // STYLE 2 PARALLAX (Bottom overlay with bump + top overlay)
    // ========================================================================

    handleStyle2TopOverlay(image, parentRect, windowHeight) {
      // Calculate immediate scroll progress without the bump delay
      const immediateScrollProgress = Math.min(1, Math.max(0, -parentRect.top / windowHeight))
      const parallaxX = immediateScrollProgress * 200 // Move left by up to 200px
      const parallaxY = -immediateScrollProgress * 1000
      image.style.transform = `translateX(${parallaxX}px) translateY(${parallaxY}px)`
    }
  }

  customElements.define('slideshow-component', SlideshowComponent)
}

// ============================================================================
// HERO SLIDESHOW PARALLAX EFFECTS
// ============================================================================

class HeroSlideshowParallax {
  constructor() {
    this.slideshowComponents = document.querySelectorAll('slideshow-component')
    this.init()
  }

  init() {
    this.slideshowComponents.forEach(slideshow => {
      this.setupParallaxForSlideshow(slideshow)
    })
  }

  setupParallaxForSlideshow(slideshow) {
    // Find all overlay images in this slideshow
    const bottomOverlays = slideshow.querySelectorAll('.hero-slideshow-overlay-image-bottom img')

    // Check if this slideshow has style classes
    const slides = slideshow.querySelectorAll('.f-slideshow__slide')
    const hasStyle1 = Array.from(slides).some(slide =>
      slide.classList.contains('hero-slideshow--style_1')
    )
    const hasStyle2 = Array.from(slides).some(slide =>
      slide.classList.contains('hero-slideshow--style_2')
    )

    // Setup parallax for bottom overlays
    bottomOverlays.forEach((overlay, index) => {
      const parent = overlay.closest('.hero-slideshow-overlay-image-bottom')

      if (hasStyle1) {
        this.setupStyle1Parallax(overlay, parent)
      }
      if (hasStyle2) {
        this.setupStyle2Parallax(overlay, parent)
      }
    })
  }

  setupStyle1Parallax(image, parent) {
    if (!image || !parent) return

    const handleParallax = () => {
      this.handleParallax1({ image, parent })
    }

    window.addEventListener('scroll', handleParallax)
    window.addEventListener('resize', handleParallax)
    handleParallax() // Run on load
  }

  setupStyle2Parallax(image, parent) {
    if (!image || !parent) return

    const handleParallax = () => {
      this.handleParallax2({ image, parent })
    }

    window.addEventListener('scroll', handleParallax)
    window.addEventListener('resize', handleParallax)
    handleParallax() // Run on load
  }

  // ========================================================================
  // STYLE 1 PARALLAX (Bottom overlay with rotation)
  // ========================================================================

  handleParallax1(style1Elements) {
    const { image, parent } = style1Elements
    if (!image || !parent) return

    const parentRect = parent.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const parentHeight = parent.offsetHeight

      // Only do the effect when the parent is in the viewport
      if (parentRect.bottom > 0 && parentRect.top < windowHeight) {
      const totalScrollable = windowHeight + parentHeight
      const parentTopInView = Math.max(0, windowHeight - parentRect.top)
      const scrollProgress = Math.min(1, Math.max(0, parentTopInView / totalScrollable))

      // Parallax movement: from 0px to -200px
      const minOffset = 0
      const maxOffset = -200
      const parallaxY = minOffset + (maxOffset - minOffset) * scrollProgress

      image.style.transform = `translateY(${parallaxY}px) rotate(180deg)`
      } else {
      // Reset if parent is not in view
      image.style.transform = `translateY(0px) rotate(180deg)`
      }
    }

  // ========================================================================
  // STYLE 2 PARALLAX (Bottom overlay with bump + top overlay)
  // ========================================================================

  handleParallax2(style2Elements) {
    const { image, parent } = style2Elements
    if (!image || !parent) return

    const parentRect = parent.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const parentHeight = parent.offsetHeight

    // If parent top is below the top of the viewport, keep images in original position
    if (parentRect.top > 0) {
      image.style.transform = `translateX(-50%) translateY(0px)`
      return
    }

    // If parent is in the viewport
    if (parentRect.bottom > 0) {
      // Linear scroll progress: 0 (just entering) to 1 (fully out)
      const scrollableDistance = parentHeight + parentRect.top
      const scrollProgress = Math.min(1, Math.max(0, -parentRect.top / scrollableDistance))
      // Move out of view by up to 20% of parent height (adjust as needed)
      const parallaxY = scrollProgress * parentHeight * 0.1
      image.style.transform = `translateX(-50%) translateY(${parallaxY}px)`
    } else {
      // If parent is out of view, move images out of sight
      image.style.transform = `translateX(-50%) translateY(${parentHeight * 0.2}px)`
    }
  }

  calculateStyle2ParallaxY(scrollProgress, bumpProgressEnd, bumpUp, parallaxDistance) {
        if (scrollProgress < bumpProgressEnd) {
          // Ease out bump over the first bumpProgressEnd of scroll
      const bumpProgress = scrollProgress / bumpProgressEnd
      const eased = 1 - Math.pow(1 - bumpProgress, 2)
      return -bumpUp * eased
        } else {
          // After bump, scroll down and out, but slower and not all the way
      const afterBumpProgress = (scrollProgress - bumpProgressEnd) / (1 - bumpProgressEnd)
      return -bumpUp + afterBumpProgress * parallaxDistance
    }
  }

  handleStyle2TopOverlay(image, parentRect, windowHeight) {
    // Calculate immediate scroll progress without the bump delay
    const immediateScrollProgress = Math.min(1, Math.max(0, -parentRect.top / windowHeight))
    const parallaxX = immediateScrollProgress * 200 // Move left by up to 200px
    const parallaxY = -immediateScrollProgress * 1000
    image.style.transform = `translateX(${parallaxX}px) translateY(${parallaxY}px)`
      }
    }

// ============================================================================
// CUSTOM FLICKITY NAVIGATION BUTTONS
// ============================================================================

class CustomFlickityNavigation {
  constructor() {
    this.init()
  }

  init() {
    // Find all sections with custom navigation buttons
    const sectionsWithCustomNav = document.querySelectorAll('[data-custom-flickity-nav]')

    console.log('CustomFlickityNavigation: Found', sectionsWithCustomNav.length, 'sections with custom nav')

    sectionsWithCustomNav.forEach(section => {
      console.log('CustomFlickityNavigation: Processing section:', section)
      this.setupCustomNavigation(section)
    })
  }

  setupCustomNavigation(section) {
    const flickityComponent = section.querySelector('flickity-component')

    if (!flickityComponent) return

    const prevButtons = section.querySelectorAll('.custom-flickity-prev')
    const nextButtons = section.querySelectorAll('.custom-flickity-next')

    // Only proceed if we actually have custom navigation buttons
    if (prevButtons.length === 0 && nextButtons.length === 0) return

    // Wait for Flickity to be initialized
    const checkFlickity = setInterval(() => {
      const flkty = flickityComponent.slider && flickityComponent.slider.instance

      if (flkty && typeof flkty === 'object') {
        clearInterval(checkFlickity)

        // Wire up all prev buttons
        prevButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault()
            flkty.previous()
          })
        })

        // Wire up all next buttons
        nextButtons.forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault()
            flkty.next()
          })
        })

        // Only hide default Flickity buttons if we have custom buttons
        if (prevButtons.length > 0 || nextButtons.length > 0) {
          const defaultButtons = flickityComponent.querySelectorAll('.flickity-prev-next-button')
          defaultButtons.forEach(button => {
            button.style.display = 'none'
          })
        }
      }
    }, 100)
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', function () {
  // Initialize hero slideshow parallax effects
  new HeroSlideshowParallax()

  // Initialize custom Flickity navigation with a slight delay
  setTimeout(() => {
    new CustomFlickityNavigation()
  }, 100)
})